import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Button } from '@blueprintjs/core';
import './CollapsibleSection.css';

/**
 * A collapsible sidebar that can hold draggable feature cards. The section can be positioned 
 * on any side of the parent container (top, bottom, left, right) and allows users to 
 * reorder its content using drag-and-drop functionality. It also saves the order of the cards 
 * to localStorage, allowing the layout to persist across sessions.
 * Props:
 * @param {boolean} collapsible - Determines whether the section can be collapsed or not. Default is `false`.
 * @param {string} position - Specifies the position of the section: "top", "bottom", "left", or "right".
 * @param {string} title - The title displayed at the top of the section.
 * @param {number} size - The size of the section when expanded. Default is 140px.
 * @param {React.ReactNode} children - The content of the section, typically `FeatureCard` components.
 *
 * State:
 * @param {boolean} collapsed - Indicates whether the section is collapsed or expanded.
 * @param {Array} cards - An array of the feature cards within the section, each having a unique ID and content.
 *
 * Example Usage:
 * ```
 * <CollapsibleSection position="left" title="My Section" collapsible={true}>
 *    <FeatureCard title="Card 1">Content 1</FeatureCard>
 *    <FeatureCard title="Card 2">Content 2</FeatureCard>
 * </CollapsibleSection>
 * ```
 *
 * @returns {JSX.Element} The rendered `CollapsibleSection` component.
 */
const CollapsibleSection = ({ collapsible = false, position, title, size = 140, children }) => {
    const [collapsed, setCollapsed] = React.useState(false);

    // Helper function to save only card IDs to localStorage
    const saveCardsToLocalStorage = (cards) => {
        const cardIds = cards.map((card) => card.id);
        localStorage.setItem(`${title}-cards`, JSON.stringify(cardIds));
    };

    // Load initial card order from localStorage if it exists, otherwise use default order
    const loadCardsFromLocalStorage = () => {
        const savedCardIds = localStorage.getItem(`${title}-cards`);

        const initialCardsArray = React.Children.toArray(children).map((child, index) => ({
            id: `card-${index}`,
            content: child,
        }));

        if (savedCardIds) {
            const savedCardIdsArray = JSON.parse(savedCardIds);

            const savedCardIdsSet = new Set(savedCardIdsArray);

            const updatedCards = initialCardsArray
                .filter(card => savedCardIdsSet.has(card.id))
                .map(card => ({
                    id: card.id,
                    content: card.content,
                }));

            const newCards = initialCardsArray
                .filter(card => !savedCardIdsSet.has(card.id))
                .map(card => ({
                    id: card.id,
                    content: card.content,
                }));

            return [...updatedCards, ...newCards];
        } else {
            return initialCardsArray;
        }
    };

    // Convert children (=FeatureCards) into an array so they can be reordered
    const initialCards = React.Children.toArray(children).map((child, index) => ({
        id: `card-${index}`,
        content: child,
    }));
    const [cards, setCards] = useState(loadCardsFromLocalStorage);

    // save card order
    useEffect(() => {
        saveCardsToLocalStorage(cards);
    }, [cards]);

    const toggleCollapse = () => {
        if (collapsible) {
            setCollapsed(!collapsed);
        }
    };

    const handleOnDragEnd = (result) => {
        if (!result.destination) return;

        const items = Array.from(cards);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);

        // Update the state and save the new order to localStorage
        setCards(items);
        saveCardsToLocalStorage(items);
    };

    const getIcon = () => {
        switch (position) {
            case 'top':
                return collapsed ? 'chevron-down' : 'chevron-up';
            case 'bottom':
                return collapsed ? 'chevron-up' : 'chevron-down';
            case 'left':
                return collapsed ? 'chevron-right' : 'chevron-left';
            case 'right':
                return collapsed ? 'chevron-left' : 'chevron-right';
            default:
                return 'chevron-down';
        }
    };

    const sectionStyle = {
        ...(position === 'top' || position === 'bottom'
            ? {
                //height: collapsed ? 'auto' : `${size}px`,
                height: `auto`,
                width: '100%',
            //    flexShrink: 0,
            }
            : {
                width: collapsed ? 'auto' : `${size}px`,
                height: '100%',
/*                flexShrink: 0,*/
            }),
        display: 'flex',
        flexDirection: (position === 'top' || position === 'bottom') ? 'column' : 'column',
    };

    // Determine drag & drop direction based on section position
    const direction = position === 'top' || position === 'bottom' ? 'horizontal' : 'vertical';

    return (
        <div
            className={`collapsible-section ${position} ${collapsed ? 'collapsed' : ''}`}
            style={sectionStyle}
        >
            <div className="collapsible-header">
                <span>{title}</span>
                {collapsible && (
                    <Button
                        icon={getIcon()}
                        onClick={toggleCollapse}
                        className="toggle-button"
                        minimal={false}
                    />
                )}
            </div>

            <DragDropContext onDragEnd={handleOnDragEnd}>
                <Droppable droppableId="droppable-cards" direction={direction}>
                    {(provided) => (
                        <div
                            className="content"
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                        >
                            {cards.map((card, index) => (
                                <Draggable key={card.id} draggableId={card.id} index={index}>
                                    {(provided) => (
                                        <div
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                        >
                                            {React.cloneElement(card.content, {
                                                dragHandleProps: provided.dragHandleProps,
                                            })}
                                        </div>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>

        </div >
    );
};

export default CollapsibleSection;